// 根据用户描述与拓扑类型构造大模型提示词，约束其输出严格的 G6 图 JSON。

import { NODE_MODEL_LIBRARY } from '../graph/modelLibrary'
import { NODE_CATEGORIES, STATUS_VALUES, TOPOLOGY_TYPES } from './topology.js'

const TYPE_LABEL = Object.fromEntries(
  TOPOLOGY_TYPES.map((t) => [t.value, t.label])
)

// 各拓扑类型建议使用的节点分类，帮助模型选型（非强制）。
const TYPE_CATEGORY_HINT = {
  drone: 'station, satellite, drone, terminal',
  communication: 'router, switch, gateway, terminal',
  microservice: 'gateway, service, storage',
  machinery: 'controller, actuator, sensor, terminal'
}

export function buildSystemPrompt() {
  return [
    '你是网络拓扑建模助手。请把用户的自然语言描述转换为可视化拓扑图数据。',
    '只输出一个 JSON 对象，不要输出任何解释、注释或 Markdown 代码块。',
    '',
    'JSON 结构：',
    '{',
    '  "nodes": [',
    '    { "id": "唯一英文/数字ID", "label": "显示名", "category": "分类",',
    '      "status": "状态", "meta": { "指标名": "指标值" },',
    '      "routes": [ { "destination_nodes": ["目标节点ID"], "egress_interface": "出接口ID", "next_hop": "下一跳节点ID" } ] }',
    '  ],',
    '  "edges": [',
    '    { "source": "源节点ID", "target": "目标节点ID", "linkId": "信道编号(可选)",',
    '      "linkType": "wired|wireless", "label": "边标签", "value": 数字(约等于Mbps的容量/吞吐),',
    '      "status": "状态", "meta": { "指标名": "指标值" },',
    '      "sourceNic": { "id": "源接口ID(可选,如 N0011:IF000002)", "meta": { "指标名": "指标值" } },',
    '      "targetNic": { "id": "目标接口ID(可选)", "meta": { "指标名": "指标值" } } }',
    '  ]',
    '}',
    '',
    'nodes 数组只放"设备节点"（路由器、交换机、终端等）。接口/网卡不是节点，',
    '绝不能出现在 nodes 里；接口只能作为 edge 的 sourceNic/targetNic 出现。',
    `node.category 必须取自：${NODE_CATEGORIES.join(', ')}。`,
    `可用设备外观型号：${JSON.stringify(Object.fromEntries(Object.entries(NODE_MODEL_LIBRARY).map(([category, models]) => [category, models.map(({ id, name }) => ({ id, name }))])))}。`,
    '设备外观必须与实际设备相符，不能仅凭 category 强行套用型号；匹配时填写对应 node.modelId，不匹配时填写 node.modelId="default"。无人车使用 category=terminal、modelId="ugv_patrol"；无人狗使用 category=actuator、modelId="robot_dog"，并在 meta.设备类型中保留真实设备类型。其他未收录设备使用 default。',
    `status 必须取自：${STATUS_VALUES.join(', ')}（正常用 normal，可省略）。`,
    'edge.source / edge.target 必须引用已存在的 node.id。',
    'meta 使用中文键值，尽量包含描述中出现的可观测指标（速率、时延、丢包、队列、信号、错误率等）。',
    // 网卡(接口)明细：诊断的饱和/降级判定依赖接口级速率与包计数，尽量提供。
    'sourceNic/targetNic 是这条链路两端节点上的网卡(接口)。若描述含接口级数据，请填写：',
    '发送侧(sourceNic)放"发送速率"(Mbps)、"发送数据包"(累计条数)、"队列容量"、"当前排队数据包"；',
    '接收侧(targetNic)放"接收速率"(Mbps)、"接收数据包"(累计条数)。无接口级数据时可省略这两个字段。',
    // 路由表：诊断可据此推导受影响的下游节点，有则提供。
    'node.routes 是该节点的路由表(可选)：每条含 destination_nodes(可达的目标节点ID数组)、',
    'egress_interface(出接口ID，对应某条边的 sourceNic/targetNic 的 id)、next_hop(下一跳节点ID)。',
    '仅当能从描述推断路由时提供，否则省略 routes。',
    'linkId 用于有明确信道/链路编号时（如 C0006、WC0002）。',
    '至少包含描述中提到的全部实体与连接；ID 保持稳定、简洁。'
  ].join('\n')
}

export function buildUserPrompt({ text, type }) {
  const label = TYPE_LABEL[type] || type || '通用网络'
  const hint = TYPE_CATEGORY_HINT[type]
  const lines = [`拓扑类型：${label}`]
  if (hint) lines.push(`该类型常用节点分类：${hint}`)
  lines.push('', '自然语言描述：', String(text || '').trim() || '（未提供描述，请生成一个该类型的合理示例拓扑）')
  return lines.join('\n')
}
